import { useEffect } from "react";

const CANONICAL_ORIGIN = "https://chrissavalanti.com";
const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/og-image.jpg`;

/**
 * Set/replace a <meta> tag in <head>. Matches by attribute (name|property).
 */
function setMeta(attr, value, content) {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${value}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function setLink(rel, href) {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

/**
 * useSEO — sets per-route document.title, description, canonical and OG/Twitter tags.
 * Pure side-effect hook. No new dependency.
 */
export function useSEO({ title, description, path, image }) {
    useEffect(() => {
        const url = `${CANONICAL_ORIGIN}${path || "/"}`;
        const ogImage = image || DEFAULT_OG_IMAGE;

        if (title) document.title = title;
        setMeta("name", "description", description);

        setLink("canonical", url);

        setMeta("property", "og:url", url);
        setMeta("property", "og:title", title);
        setMeta("property", "og:description", description);
        setMeta("property", "og:image", ogImage);
        setMeta("property", "og:type", "website");
        setMeta("property", "og:site_name", "Chrissa Valanti");

        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", title);
        setMeta("name", "twitter:description", description);
        setMeta("name", "twitter:image", ogImage);
    }, [title, description, path, image]);
}

export const SEO = {
    home: {
        title: "Chrissa Valanti — Begin From Where You Are",
        description:
            "Singer, mother, founder and two-time blood cancer survivor Chrissa Valanti shares the story of finding her voice and building again.",
        path: "/",
    },
    journey: {
        title: "My Journey — Chrissa Valanti",
        description:
            "Chrissa Valanti's journey through music, cancer, grief, recovery and the decision to begin building again.",
        path: "/journey",
    },
    building: {
        title: "What I'm Building — Chrissa Valanti",
        description:
            "Explore Valanti Labs, ALATI and Chrissa Valanti's return to music—three ventures shaped by lived experience.",
        path: "/building",
    },
    labs: {
        title: "Valanti Labs — Rebuilding Confidence Through Movement",
        description:
            "Valanti Labs is developing intelligent video-based movement technology inspired by the loss of confidence people can experience in their bodies.",
        path: "/building/valanti-labs",
    },
    alati: {
        title: "ALATI — A Mother-and-Son Soap Business",
        description:
            "ALATI brings together Greek heritage, traditional soap-making, carefully considered ingredients and a business built by a mother and son.",
        path: "/building/alati",
    },
    music: {
        title: "Music — Finding My Voice Again | Chrissa Valanti",
        description:
            "Chrissa Valanti's return to music is a story of grief, identity and finding her voice again.",
        path: "/building/music",
    },
    media: {
        title: "Media and Speaking — Chrissa Valanti",
        description:
            "Biography, speaking subjects and media enquiries for singer, founder and two-time blood cancer survivor Chrissa Valanti.",
        path: "/media",
    },
    contact: {
        title: "Contact — Chrissa Valanti",
        description:
            "Contact Chrissa Valanti regarding media, speaking, music, Valanti Labs, ALATI or general enquiries.",
        path: "/contact",
    },
    privacy: {
        title: "Privacy Policy — Chrissa Valanti",
        description:
            "How personal information is collected, stored and handled through the Chrissa Valanti website.",
        path: "/privacy",
    },
    terms: {
        title: "Terms of Use — Chrissa Valanti",
        description:
            "Terms governing access to and use of the Chrissa Valanti personal website.",
        path: "/terms",
    },
    journal: {
        title: "Journal — Chrissa Valanti",
        description:
            "Personal reflections and founder updates from Chrissa Valanti.",
        path: "/journal",
    },
};
